import React from 'react'

import { buildComponent } from '../../../../components/factory'
import { graphql } from '../../../../services/graphql'
import { Category } from '../../../../state/models/audio'
import { useStoreState } from '../../../../hooks/state'

import * as UI from '@mantine/core'
import * as Semantics from '../../../../components/semantics'

const ShowUniqueIdentifier = buildComponent<{ slug?: string }>().withRender(
  ({ props }) => (
    <UI.Group>
      <UI.Text weight={700}>Identifiant unique</UI.Text>
      <UI.Kbd mb={-4} px={16}>
        {props.slug ?? '...'}
      </UI.Kbd>
    </UI.Group>
  ),
)

const EditName = buildComponent<{
  short?: { fr: string; en: string }
  long?: { fr: string; en: string }
  onUpdate: (updates: Partial<Category>) => void
}>()
  .withLifecycle(({ props }) => ({
    updateShort: (short: { fr: string; en: string }) => {
      props.onUpdate({ short })
    },
    updateLong: (long: { fr: string; en: string }) => {
      props.onUpdate({ long })
    },
  }))
  .withRender(({ props, lifecycle }) => (
    <UI.Stack sx={{ width: '100%' }} spacing={32}>
      <UI.Input.Wrapper
        sx={{ flex: 1, width: '100%' }}
        label={<b style={{ fontSize: '2rem' }}>Titre de la catégorie</b>}
        description="Nom court de la catégorie affiché dans l'interface"
      >
        <Semantics.LocalizedInput
          value={props.short}
          onChange={lifecycle.updateShort}
        />
      </UI.Input.Wrapper>
      <UI.Input.Wrapper
        sx={{ flex: 1, width: '100%' }}
        label={<b style={{ fontSize: '2rem' }}>Sous-titre</b>}
        description="Sous-titre de la catégorie pour donner plus de contexte"
      >
        <Semantics.LocalizedInput
          value={props.long}
          onChange={lifecycle.updateLong}
        />
      </UI.Input.Wrapper>
    </UI.Stack>
  ))

const EditType = buildComponent<{
  type?: 'theme' | 'book' | 'background'
  onUpdate: (updates: Partial<Category>) => void
}>().withRender(({ props }) => (
  <UI.Input.Wrapper
    sx={{ width: '100%' }}
    label={<b style={{ fontSize: '2rem' }}>Type</b>}
    description="Type de contenu que cette catégorie peut contenir"
  >
    <UI.Group mt={8}>
      <UI.Button
        style={{ minHeight: '4rem', width: '16rem' }}
        color={props.type === 'theme' ? undefined : 'gray'}
        onClick={() => props.onUpdate({ type: 'theme' })}
      >
        Thématique
      </UI.Button>
      <UI.Button
        style={{ minHeight: '4rem', width: '16rem' }}
        color={props.type === 'book' ? undefined : 'gray'}
        onClick={() => props.onUpdate({ type: 'book' })}
      >
        Audiobook
      </UI.Button>
      <UI.Button
        style={{ minHeight: '4rem', width: '16rem' }}
        color={props.type === 'background' ? undefined : 'gray'}
        onClick={() => props.onUpdate({ type: 'background' })}
      >
        Fond sonore
      </UI.Button>
    </UI.Group>
  </UI.Input.Wrapper>
))

const EditParent = buildComponent<{
  parent?: Partial<Category>
  categoryId: string
  type?: 'theme' | 'book' | 'background'
  onUpdate: (updates: Partial<Category>) => void
}>()
  .withLifecycle(({ props }) => {
    const allCategories = useStoreState((store) => store.audio.categories)

    // Fonction récursive pour trouver tous les descendants d'une catégorie
    const findDescendants = (
      categoryId: string,
      visited = new Set<string>(),
    ): Set<string> => {
      if (visited.has(categoryId)) {
        return visited // Éviter les boucles infinies
      }

      visited.add(categoryId)

      // Trouver tous les enfants directs
      const directChildren = allCategories.filter(
        (cat: Category) => cat.parent?._id === categoryId,
      )

      // Ajouter récursivement tous les descendants
      directChildren.forEach((child) => {
        findDescendants(child._id, visited)
      })

      return visited
    }

    // Obtenir tous les descendants de la catégorie actuelle
    const descendants = findDescendants(props.categoryId)

    // Filtrer les parents disponibles
    const availableParents = allCategories.filter((cat: Category) => {
      // Exclure la catégorie elle-même
      if (cat._id === props.categoryId) {
        return false
      }

      // Exclure tous les descendants (empêche les références cycliques)
      if (descendants.has(cat._id)) {
        return false
      }

      // Si un type est sélectionné, ne montrer que les catégories du même type
      if (props.type && cat.type !== props.type) {
        return false
      }

      // Pour éviter les hiérarchies à plus de 2 niveaux,
      // ne permettre que les catégories qui n'ont pas de parent (catégories de niveau 1)
      if (cat.parent) {
        return false
      }

      return true
    })

    return { availableParents }
  })
  .withRender(({ props, lifecycle }) => {
    const selectedValue = props.parent?._id ? String(props.parent._id) : null
    const isDisabled = !props.type

    return (
      <UI.Input.Wrapper
        sx={{ width: '100%' }}
        label={<b style={{ fontSize: '2rem' }}>Catégorie parente</b>}
        description={
          isDisabled
            ? "Veuillez d'abord sélectionner un type pour cette catégorie"
            : 'Catégorie parente pour organiser hiérarchiquement (optionnel)'
        }
      >
        <UI.Select
          mt={8}
          placeholder={
            isDisabled
              ? "Sélectionnez d'abord un type"
              : 'Aucune catégorie parente'
          }
          disabled={isDisabled}
          clearable
          value={selectedValue}
          onChange={(value) =>
            props.onUpdate({
              parent: value
                ? {
                    _id: value,
                    short: { fr: '', en: '' },
                    long: { fr: '', en: '' },
                  }
                : undefined,
            })
          }
          data={lifecycle.availableParents.map((cat: Category) => ({
            value: String(cat._id),
            label: cat.short?.fr || cat.short?.en || cat.slug,
          }))}
        />
      </UI.Input.Wrapper>
    )
  })

export const SectionMain = buildComponent<{ categoryId: string }>()
  .withLifecycle(({ props }) => {
    const allCategories = useStoreState((store) => store.audio.categories)
    const category = allCategories.find(
      (c: Category) => c._id === props.categoryId,
    )

    const [loading, setLoading] = React.useState(false)

    const update = async (updates: Partial<Category>) => {
      if (!category) {
        return
      }

      try {
        setLoading(true)

        // Convert parent object to string ID for GraphQL
        const graphqlUpdates: any = {
          _id: category._id,
          ...updates,
        }

        if ('parent' in updates) {
          graphqlUpdates.parent = updates.parent?._id || undefined
        }

        await graphql.mutations.updateCategory(graphqlUpdates)
      } finally {
        setLoading(false)
      }
    }

    return { category, loading, update }
  })
  .withRender(({ props, lifecycle }) => (
    <UI.Stack sx={{ flex: 1 }} spacing={24}>
      <ShowUniqueIdentifier slug={lifecycle.category?.slug} />
      <EditName
        short={lifecycle.category?.short}
        long={lifecycle.category?.long}
        onUpdate={lifecycle.update}
      />
      <EditType type={lifecycle.category?.type} onUpdate={lifecycle.update} />
      <EditParent
        parent={lifecycle.category?.parent}
        categoryId={props.categoryId}
        type={lifecycle.category?.type}
        onUpdate={lifecycle.update}
      />

      {lifecycle.loading && (
        <UI.Loader
          sx={{ position: 'absolute', top: '1rem', right: '1rem' }}
          size="xs"
        />
      )}
    </UI.Stack>
  ))
